import classNames from "classnames";
import { debounce, get, isEmpty } from "lodash";
import Link from "next/link";
import { FC, Fragment, useCallback, useId, useMemo, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import Avatar from "~/components/Avatar";
import Divider from "~/components/Divider";
import Drawer from "~/components/Drawer";
import Text from "~/components/Text";
import Icon from "~/components/Icon";
import Colors from "~/constants/colors";
import { useMegaMenu } from "~/data/mega-menu/useMegaMenu";
import { UserAtom } from "~/services/user";
import { transformNodesToMenu } from "~/utils/transform";
import styles from "./HeaderTwo.module.scss";
import { MenuTransformProps } from "./interfaces";
import Skeleton from "react-loading-skeleton";
import { MegaMenuType, PATH } from "~/constants/enum";
import Input from "~/components/Input";
import { ALL_VISIBLE_THEMES, getTheme, SearchPlaceholder } from "~/utils/theme";
import { recentKeywordState } from "~/services/recent-search/recent-search.atom";
import { useRouter } from "next/router";
import { useQuery } from "@apollo/client";
import { SEARCH_KEYWORD_SUGGESTION } from "~/data/search/gql";

interface MobileHeaderProps {
  open: boolean;
  onClose: () => void;
}

interface TreeMenuItemProps {
  item: MenuTransformProps;
  onClose: () => void;
  level?: number;
}

const TreeMenuItem: FC<TreeMenuItemProps> = ({ item, onClose, level = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = !isEmpty(item.children);
  const { title, url, type, content } = item;

  // Handle CMS block types
  if (type === MegaMenuType.CmsBlock && content) {
    return (
      <div
        className={classNames("p-3", {
          "pl-6": level === 1,
          "pl-9": level === 2,
        })}
      >
        <div dangerouslySetInnerHTML={{ __html: content }} />
      </div>
    );
  }

  const href =
    type === MegaMenuType.Category
      ? PATH.CategoryDetail.replace("[slug]", url)
      : type === MegaMenuType.CmsPage
        ? `/pages/${url}`
        : url || "";

  const toggleExpand = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    if (!hasChildren && href) {
      onClose();
    } else if (hasChildren) {
      e.preventDefault();
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div className="w-full">
      <div
        className={classNames(
          "flex items-center justify-between py-3 px-3 hover:bg-gray-50 cursor-pointer",
          { "pl-6": level === 1, "pl-9": level === 2, "pl-12": level >= 3 }
        )}
        onClick={handleLinkClick}
      >
        <div className="flex items-center gap-2 flex-1">
          {href ? (
            <Link href={href} className="flex-1 text-left">
              <Text
                type={level === 0 ? "title-5" : "label-1"}
                className="!text-sm uppercase"
              >
                {title}
              </Text>
            </Link>
          ) : (
            <Text
              type={level === 0 ? "title-5" : "label-1"}
              className="!text-sm uppercase flex-1"
            >
              {title}
            </Text>
          )}
          {hasChildren && (
            <Icon
              name="arrow-right1"
              size={16}
              className={classNames(
                "text-gray-500 transition-transform duration-200",
                { "rotate-90": isExpanded }
              )}
            />
          )}
        </div>
      </div>

      {hasChildren && (
        <div
          className={classNames(
            "overflow-hidden transition-all duration-300",
            isExpanded ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
          )}
        >
          <div className="border-l-2 border-gray-200 ml-4">
            {item.children.map((child, index) => (
              <TreeMenuItem
                key={`${child.nodeId}-${index}`}
                item={child}
                onClose={onClose}
                level={level + 1}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MobileHeaderTwo: FC<MobileHeaderProps> = ({ open, onClose }) => {
  const id = useId();
  const currentUser = useRecoilValue(UserAtom.currentUser);
  const setRecoilRecentKeyword = useSetRecoilState(recentKeywordState);
  const router = useRouter();

  const [idReset, setIdReset] = useState(id);
  const [tempValue, setTempValue] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [debounceSearchValue, setDebounceSearchValue] = useState<string>("");

  const { nodes: megaMenu, loading } = useMegaMenu({
    identifiers: "menu-mobile",
  });

  const { data: searchSuggestionV2 } = useQuery(SEARCH_KEYWORD_SUGGESTION, {
    variables: {
      keyword: debounceSearchValue,
    },
    onError: ({ message }) => console.error(message, "SEARCH_SUGGESTION"),
    skip: isEmpty(debounceSearchValue),
  });

  const menu = useMemo(() => {
    if (loading) return Array.from({ length: 5 });
    return isEmpty(megaMenu) ? [] : transformNodesToMenu(megaMenu);
  }, [loading, megaMenu]) as MenuTransformProps[];

  const handleCloseSearch = () => {
    setSearchValue("");
    setDebounceSearchValue("");
  };

  const handleSearchProduct = (value: string) => {
    if (isEmpty(value)) return;
    setRecoilRecentKeyword?.((prev) => {
      if (isEmpty(value) || prev.includes(value)) {
        return prev;
      }
      return [value, ...prev];
    });
    handleCloseSearch();

    router.push({
      pathname: "/search",
      query: {
        keyword: value,
      },
    });
    // setOpenSearch(false);
  };

  const handleKeyDownSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const suggestions = get(searchSuggestionV2, "SearchKeywordSuggestion", []);
    if (e.key === "ArrowDown") {
      setFocusedIndex((prevIndex) => (prevIndex + 1) % suggestions.length);
      setTempValue(suggestions[(focusedIndex + 1) % suggestions.length]);
    } else if (e.key === "ArrowUp") {
      setFocusedIndex(
        (prevIndex) => (prevIndex - 1 + suggestions.length) % suggestions.length
      );
      setTempValue(
        suggestions[
          (focusedIndex - 1 + suggestions.length) % suggestions.length
        ]
      );
    } else if (e.key === "Enter" && focusedIndex >= 0) {
      handleSearchProduct(suggestions[focusedIndex]);
      setSearchValue(suggestions[focusedIndex]);
      setFocusedIndex(-1);
      setTempValue("");
    } else if (e.key === "Enter") {
      handleSearchProduct(searchValue);
    }
  };

  const debounceSearchHandler = useCallback(
    debounce((searchValue: string) => {
      setDebounceSearchValue(searchValue);
    }, 500),
    []
  );

  const handleChangeSearchValue = (value: string) => {
    setSearchValue(value);
    setFocusedIndex(-1);
    setTempValue("");
    debounceSearchHandler(value);
  };

  return (
    <Drawer
      open={open}
      onClose={() => {
        onClose();
        setIdReset((prev) => prev + 1);
      }}
      className={classNames(styles.mobile_header, "!bg-[#F2F2F2]")}
      titleClassName={classNames("items-center px-3", styles.drawer_title)}
      title={
        isEmpty(currentUser?.token) ? (
          <div className="flex items-center gap-3"></div>
        ) : (
          <Avatar name={currentUser?.firstname || "-"} />
        )
      }
      iconClassName={styles.drawer_icon}
    >
      <div className="p-3">
        <Input
          placeholder={
            SearchPlaceholder[getTheme() as ALL_VISIBLE_THEMES] ??
            "Search Everything Here"
          }
          value={tempValue || searchValue}
          onChange={(e) => handleChangeSearchValue(e.target.value)}
          onKeyDown={handleKeyDownSearch}
          onClearText={() => handleChangeSearchValue("")}
          prefix={
            <Icon
              name="search-normal-1"
              color={Colors.GREY_400}
              size={20}
              onKeyDown={handleKeyDownSearch}
              className="cursor-pointer"
              onClick={() => handleSearchProduct(searchValue)}
            />
          }
        />
      </div>
      <Divider dividerColor={Colors.GREY_100} />
      <div
        className="flex flex-col flex-1 overflow-auto"
        key={idReset}
        style={{ paddingBottom: 70 }}
      >
        {menu.map((item, index) => {
          return (
            <Fragment key={index}>
              {loading ? (
                <div className="px-3 py-2">
                  <Skeleton height={40} />
                </div>
              ) : (
                <>
                  <TreeMenuItem item={item} onClose={onClose} />
                  {/* {index < menu.length - 1 && (
                    <Divider dividerColor={Colors.GREY_100} />
                  )} */}
                </>
              )}
            </Fragment>
          );
        })}
        <div className="mb-5 px-3">
          <Divider dividerColor={Colors.GREY_100} />
        </div>
        <div className="mb-5 px-3 flex flex-col gap-3">
          <Link href="/sign-in">
            <Text type="title-5">SIGN IN</Text>
          </Link>
          <Link href="/sign-up">
            <Text type="title-5">REGISTER</Text>
          </Link>
        </div>
      </div>
    </Drawer>
  );
};

export default MobileHeaderTwo;
